// ============================================
// Business Unit Performance Chart
// Comparison chart for ECOM vs CONST metrics
// Shows relevant metrics for each BU's focus
// ============================================

import { ChartSkeleton } from '../../../components/layout/LoadingState';
import { formatCurrency, formatNumber } from '../../../utils/formatters';

// Semantic colors from dashboard-design skill
const colors = {
  ecom: '#3B82F6',  // Blue - E-Commerce
  const: '#10B981', // Green - Construction
};

interface BusinessUnitData {
  ecom: {
    spend: number;
    messages: number;
    leads: number;
    impressions: number;
    clicks: number;
    costPerMessage: number;
    costPerLead: number;
  };
  const: {
    spend: number;
    messages: number;
    leads: number;
    impressions: number;
    clicks: number;
    costPerMessage: number;
    costPerLead: number;
  };
}

interface BusinessUnitChartProps {
  data?: BusinessUnitData;
  isLoading: boolean;
}

export function BusinessUnitChart({ data, isLoading }: BusinessUnitChartProps) {
  if (isLoading) {
    return <ChartSkeleton height={400} />;
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 h-[400px] flex items-center justify-center">
        <p className="text-gray-500">Không có dữ liệu Business Unit</p>
      </div>
    );
  }

  // Only compare metrics that make sense for both BUs
  const comparisonMetrics = [
    {
      label: 'Chi tiêu',
      ecom: data.ecom.spend,
      const: data.const.spend,
      format: formatCurrency,
    },
    {
      label: 'Impressions',
      ecom: data.ecom.impressions,
      const: data.const.impressions,
      format: formatNumber,
    },
    {
      label: 'Clicks',
      ecom: data.ecom.clicks,
      const: data.const.clicks,
      format: formatNumber,
    },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">So Sánh Business Unit</h3>
        <p className="text-sm text-gray-500">E-Commerce vs Construction</p>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.ecom }}></div>
          <span className="text-sm font-medium text-gray-700">E-Commerce</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.const }}></div>
          <span className="text-sm font-medium text-gray-700">Construction</span>
        </div>
      </div>

      {/* Comparison Bars - Common metrics */}
      <div className="space-y-4">
        {comparisonMetrics.map((metric, index) => {
          const maxValue = Math.max(metric.ecom, metric.const);
          const ecomPercent = maxValue > 0 ? (metric.ecom / maxValue) * 100 : 0;
          const constPercent = maxValue > 0 ? (metric.const / maxValue) * 100 : 0;

          return (
            <div key={index} className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-medium text-gray-700">{metric.label}</span>
              </div>
              <div className="space-y-1">
                {/* ECOM Bar */}
                <div className="flex items-center gap-2">
                  <div className="w-20 text-xs text-gray-500">E-Com</div>
                  <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                    <div
                      className="h-full rounded transition-all duration-300"
                      style={{
                        width: `${ecomPercent}%`,
                        backgroundColor: colors.ecom,
                      }}
                    ></div>
                  </div>
                  <div className="w-28 text-right text-sm font-medium text-gray-900">
                    {metric.format(metric.ecom)}
                  </div>
                </div>
                {/* CONST Bar */}
                <div className="flex items-center gap-2">
                  <div className="w-20 text-xs text-gray-500">Const</div>
                  <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
                    <div
                      className="h-full rounded transition-all duration-300"
                      style={{
                        width: `${constPercent}%`,
                        backgroundColor: colors.const,
                      }}
                    ></div>
                  </div>
                  <div className="w-28 text-right text-sm font-medium text-gray-900">
                    {metric.format(metric.const)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* BU-Specific KPIs - Each BU has different focus */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500 uppercase mb-3">KPIs theo chiến lược</p>
        <div className="grid grid-cols-2 gap-4">
          {/* E-Commerce: Focus on Messages */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-600 font-medium uppercase">E-Commerce</p>
            <p className="text-sm text-blue-500 mb-2">Mục tiêu: Messages</p>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-xs text-gray-600">Messages:</span>
                <span className="text-sm font-bold text-blue-700">{formatNumber(data.ecom.messages)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-600">Chi phí/Message:</span>
                <span className="text-sm font-bold text-blue-700">{formatCurrency(data.ecom.costPerMessage)}</span>
              </div>
            </div>
          </div>

          {/* Construction: Focus on Leads */}
          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-xs text-green-600 font-medium uppercase">Construction</p>
            <p className="text-sm text-green-500 mb-2">Mục tiêu: Leads</p>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-xs text-gray-600">Leads:</span>
                <span className="text-sm font-bold text-green-700">{formatNumber(data.const.leads)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-600">Chi phí/Lead:</span>
                <span className="text-sm font-bold text-green-700">{formatCurrency(data.const.costPerLead)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
